/**
 * Query Engine - Executes queries against tasks
 *
 * This is the single source of truth for filtering logic.
 * Used by both the main app and widgets.
 */

import type { Todo } from '../types';
import type {
  TaskQuery,
  QueryNode,
  QueryCondition,
  QueryOperator,
  DateExpression,
  FilterableField,
} from '../types/query';
import { isQueryGroup, isDateExpression } from '../types/query';

// ============================================================================
// Date Expression Resolution
// ============================================================================

/**
 * Resolve a date expression to an actual Date object
 */
export function resolveDateExpression(expr: DateExpression): Date {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (expr) {
    case '$today':
      return today;

    case '$tomorrow': {
      const d = new Date(today);
      d.setDate(d.getDate() + 1);
      return d;
    }

    case '$yesterday': {
      const d = new Date(today);
      d.setDate(d.getDate() - 1);
      return d;
    }

    case '$startOfWeek': {
      const d = new Date(today);
      d.setDate(d.getDate() - d.getDay());
      return d;
    }

    case '$endOfWeek': {
      const d = new Date(today);
      d.setDate(d.getDate() + (6 - d.getDay()));
      d.setHours(23, 59, 59, 999);
      return d;
    }

    case '$startOfMonth':
      return new Date(today.getFullYear(), today.getMonth(), 1);

    case '$endOfMonth': {
      const d = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      d.setHours(23, 59, 59, 999);
      return d;
    }

    case '$startOfYear':
      return new Date(today.getFullYear(), 0, 1);

    case '$endOfYear': {
      const d = new Date(today.getFullYear(), 11, 31);
      d.setHours(23, 59, 59, 999);
      return d;
    }

    default: {
      // Parse parameterized expressions: $daysAgo(n), $daysFromNow(n), etc.
      const daysAgoMatch = expr.match(/^\$daysAgo\((\d+)\)$/);
      if (daysAgoMatch) {
        const d = new Date(today);
        d.setDate(d.getDate() - parseInt(daysAgoMatch[1], 10));
        return d;
      }

      const daysFromNowMatch = expr.match(/^\$daysFromNow\((\d+)\)$/);
      if (daysFromNowMatch) {
        const d = new Date(today);
        d.setDate(d.getDate() + parseInt(daysFromNowMatch[1], 10));
        d.setHours(23, 59, 59, 999);
        return d;
      }

      const weeksAgoMatch = expr.match(/^\$weeksAgo\((\d+)\)$/);
      if (weeksAgoMatch) {
        const d = new Date(today);
        d.setDate(d.getDate() - parseInt(weeksAgoMatch[1], 10) * 7);
        return d;
      }

      const weeksFromNowMatch = expr.match(/^\$weeksFromNow\((\d+)\)$/);
      if (weeksFromNowMatch) {
        const d = new Date(today);
        d.setDate(d.getDate() + parseInt(weeksFromNowMatch[1], 10) * 7);
        d.setHours(23, 59, 59, 999);
        return d;
      }

      const monthsAgoMatch = expr.match(/^\$monthsAgo\((\d+)\)$/);
      if (monthsAgoMatch) {
        const d = new Date(today);
        d.setMonth(d.getMonth() - parseInt(monthsAgoMatch[1], 10));
        return d;
      }

      const monthsFromNowMatch = expr.match(/^\$monthsFromNow\((\d+)\)$/);
      if (monthsFromNowMatch) {
        const d = new Date(today);
        d.setMonth(d.getMonth() + parseInt(monthsFromNowMatch[1], 10));
        d.setHours(23, 59, 59, 999);
        return d;
      }

      // Unknown expression, return today as fallback
      console.warn(`Unknown date expression: ${expr}`);
      return today;
    }
  }
}

// ============================================================================
// Value Resolution
// ============================================================================

/**
 * Get a field value from a todo, supporting nested paths
 */
function getFieldValue(todo: Todo, field: FilterableField): unknown {
  // Direct field access - all our fields are top-level
  return (todo as Record<string, unknown>)[field];
}

/**
 * Resolve a query value, handling date expressions
 */
function resolveValue(value: unknown): unknown {
  if (isDateExpression(value)) {
    return resolveDateExpression(value);
  }
  return value;
}

/**
 * Convert a field value to Date if it's a date string.
 * Applies to all comparison and equality operators so that
 * eq/neq work correctly with date expressions like $today.
 */
function toDateIfNeeded(value: unknown, operator: QueryOperator): unknown {
  const dateOperators: QueryOperator[] = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'];
  if (dateOperators.includes(operator) && typeof value === 'string') {
    // Check if it looks like a date string
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      return new Date(value);
    }
  }
  return value;
}

// ============================================================================
// Condition Evaluation
// ============================================================================

/**
 * Evaluate a single condition against a todo
 */
function evaluateCondition(todo: Todo, condition: QueryCondition): boolean {
  const fieldValue = getFieldValue(todo, condition.field);
  const queryValue = resolveValue(condition.value);

  // Convert to dates for comparison operators if needed
  const normalizedFieldValue = toDateIfNeeded(fieldValue, condition.operator);
  const normalizedQueryValue = toDateIfNeeded(queryValue, condition.operator);

  switch (condition.operator) {
    // Comparison operators (use getTime() for Date equality since Date === Date compares references)
    case 'eq':
      if (normalizedFieldValue instanceof Date && normalizedQueryValue instanceof Date) {
        return normalizedFieldValue.getTime() === normalizedQueryValue.getTime();
      }
      return normalizedFieldValue === normalizedQueryValue;

    case 'neq':
      if (normalizedFieldValue instanceof Date && normalizedQueryValue instanceof Date) {
        return normalizedFieldValue.getTime() !== normalizedQueryValue.getTime();
      }
      return normalizedFieldValue !== normalizedQueryValue;

    case 'gt':
      if (normalizedFieldValue == null) return false;
      return (normalizedFieldValue as number | Date) > (normalizedQueryValue as number | Date);

    case 'gte':
      if (normalizedFieldValue == null) return false;
      return (normalizedFieldValue as number | Date) >= (normalizedQueryValue as number | Date);

    case 'lt':
      if (normalizedFieldValue == null) return false;
      return (normalizedFieldValue as number | Date) < (normalizedQueryValue as number | Date);

    case 'lte':
      if (normalizedFieldValue == null) return false;
      return (normalizedFieldValue as number | Date) <= (normalizedQueryValue as number | Date);

    // Set operators
    case 'in':
      if (Array.isArray(queryValue)) {
        return queryValue.includes(fieldValue);
      }
      return false;

    case 'notIn':
      if (Array.isArray(queryValue)) {
        return !queryValue.includes(fieldValue);
      }
      return true;

    // Array operators
    case 'hasAny':
      if (Array.isArray(fieldValue) && Array.isArray(queryValue)) {
        return queryValue.some(v => fieldValue.includes(v));
      }
      return false;

    case 'hasAll':
      if (Array.isArray(fieldValue) && Array.isArray(queryValue)) {
        return queryValue.every(v => fieldValue.includes(v));
      }
      return false;

    case 'isEmpty':
      if (Array.isArray(fieldValue)) {
        return fieldValue.length === 0;
      }
      return fieldValue == null || fieldValue === '';

    case 'isNotEmpty':
      if (Array.isArray(fieldValue)) {
        return fieldValue.length > 0;
      }
      return fieldValue != null && fieldValue !== '';

    // String operators
    case 'contains':
      if (typeof fieldValue === 'string' && typeof queryValue === 'string') {
        return fieldValue.toLowerCase().includes(queryValue.toLowerCase());
      }
      return false;

    case 'startsWith':
      if (typeof fieldValue === 'string' && typeof queryValue === 'string') {
        return fieldValue.toLowerCase().startsWith(queryValue.toLowerCase());
      }
      return false;

    case 'endsWith':
      if (typeof fieldValue === 'string' && typeof queryValue === 'string') {
        return fieldValue.toLowerCase().endsWith(queryValue.toLowerCase());
      }
      return false;

    // Null operators
    case 'isNull':
      return fieldValue == null || fieldValue === '';

    case 'isNotNull':
      return fieldValue != null && fieldValue !== '';

    default:
      console.warn(`Unknown operator: ${condition.operator}`);
      return true;
  }
}

/**
 * Evaluate a query node (condition or group) against a todo
 */
function evaluateNode(todo: Todo, node: QueryNode): boolean {
  if (isQueryGroup(node)) {
    if (node.conditions.length === 0) return true;

    if (node.logic === 'and') {
      return node.conditions.every(child => evaluateNode(todo, child));
    } else {
      return node.conditions.some(child => evaluateNode(todo, child));
    }
  } else {
    return evaluateCondition(todo, node);
  }
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Execute a query against a single todo
 */
export function matchesQuery(todo: Todo, query: TaskQuery | null): boolean {
  if (!query || !query.root) return true;
  return evaluateNode(todo, query.root);
}

/**
 * Filter an array of todos using a query
 */
export function executeQuery(todos: Todo[], query: TaskQuery | null): Todo[] {
  if (!query || !query.root) return todos;
  return todos.filter(todo => evaluateNode(todo, query.root!));
}

/**
 * Count todos matching a query
 */
export function countMatches(todos: Todo[], query: TaskQuery | null): number {
  if (!query || !query.root) return todos.length;
  return todos.filter(todo => evaluateNode(todo, query.root!)).length;
}

// ============================================================================
// Query Validation
// ============================================================================

export interface ValidationError {
  path: string;
  message: string;
}

/**
 * Validate a query for correctness
 */
export function validateQuery(query: TaskQuery): ValidationError[] {
  const errors: ValidationError[] = [];

  if (query.version !== 1) {
    errors.push({ path: 'version', message: `Unsupported query version: ${query.version}` });
  }

  if (query.root) {
    validateNode(query.root, 'root', errors);
  }

  return errors;
}

function validateNode(node: QueryNode, path: string, errors: ValidationError[]): void {
  if (isQueryGroup(node)) {
    if (!['and', 'or'].includes(node.logic)) {
      errors.push({ path: `${path}.logic`, message: `Invalid logic: ${node.logic}` });
    }
    node.conditions.forEach((child, i) => {
      validateNode(child, `${path}.conditions[${i}]`, errors);
    });
  } else {
    // Validate field exists
    const validFields = [
      'title', 'description', 'status', 'horizon', 'deadline',
      'created_time', 'last_modified_time', 'completed_date',
      'parent_id', 'team_id', 'assigned_to_id', 'user_id', 'tags',
      'recurrence_interval', 'notes', 'source'
    ];
    if (!validFields.includes(node.field)) {
      errors.push({ path: `${path}.field`, message: `Unknown field: ${node.field}` });
    }

    // Validate operator
    const validOperators = [
      'eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'notIn',
      'hasAny', 'hasAll', 'isEmpty', 'isNotEmpty',
      'contains', 'startsWith', 'endsWith', 'isNull', 'isNotNull'
    ];
    if (!validOperators.includes(node.operator)) {
      errors.push({ path: `${path}.operator`, message: `Unknown operator: ${node.operator}` });
    }
  }
}
